import React, { useEffect } from 'react'
import EventModal from 'components/utility/modals/EventModal'
import axios from 'axios'

// Generic NY-timer package-entry modal. It is intentionally "dumb" and
// URL-agnostic: the 1120-B plugin supplies all copy and every URL it touches
// (modalStateUrl, learnMoreUrl, ctas[].href). It never constructs plugin paths
// or knows about 1120-B, so it can be reused by other plugins/jurisdictions.
//
// On mount it auto-opens and records "shown" (so once-per-day variants suppress
// on the next visit). Each CTA either closes, dismisses (persists
// do-not-show-again), or navigates — the plugin chooses the `kind` per button.
export default function PackageEntryModal({
  suppressionKey,
  modalStateUrl,
  icon,
  title,
  titleHtml,
  titleColor,
  bodyHtml,
  learnMoreUrl,
  learnMoreLabel,
  ctas = [],
  modalStyles = null
}) {
  const modalId = `package-entry-${suppressionKey}`

  const openModal = () => window.dispatchEvent(new Event(`openModal-${modalId}`))
  const closeModal = () => window.dispatchEvent(new Event(`closeModal-${modalId}`))

  const patchState = event => {
    if (!modalStateUrl) return Promise.resolve()

    return axios
      .patch(modalStateUrl, { modal_key: suppressionKey, event })
      .catch(e => console.warn('PackageEntryModal: failed to persist modal state', e))
  }

  // Child <EventModal> registers its open/close listeners in its own mount effect,
  // which React runs before this parent effect — so the open event is caught.
  useEffect(() => {
    openModal()
    patchState('shown')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCta = cta => {
    switch (cta.kind) {
      case 'dismiss':
        patchState('dismiss')
        closeModal()
        break
      case 'navigate':
        if (cta.href) window.location.assign(cta.href)
        break
      case 'reload':
        closeModal()
        window.location.reload()
        break
      case 'close':
      default:
        closeModal()
    }
  }

  const headerContent = (
    <div className="package-entry-modal__header dome-d-flex dome-flex-column dome-gap12" style={{ textAlign: 'left', alignItems: 'flex-start' }}>
      {icon && <i className={icon} style={{ fontSize: 40, color: titleColor || undefined }} />}
      {titleHtml ? (
        <div
          className="package-entry-modal__title dome-h3"
          style={{ textAlign: 'left' }}
          dangerouslySetInnerHTML={{ __html: titleHtml }}
        />
      ) : (
        <div className="package-entry-modal__title dome-h3" style={{ textAlign: 'left', ...(titleColor ? { color: titleColor } : {}) }}>
          {title}
        </div>
      )}
    </div>
  )

  const bodyContent = (
    <div className="package-entry-modal__body dome-d-flex dome-flex-column dome-gap12" style={{ textAlign: 'left', alignItems: 'flex-start', marginTop: 18 }}>
      <div className="dome-p2 dome-color-med-grey dome-d-flex dome-flex-column dome-gap9" style={{ textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
      {learnMoreUrl && (
        <a className="package-entry-modal__law-link dome-link dome-link-aqua dome-link-arrow-right" href={learnMoreUrl} target="_blank" rel="noopener noreferrer">
          {learnMoreLabel || 'learn more'}
        </a>
      )}
    </div>
  )

  // A lone CTA (e.g. "OK") sits bottom-right per Figma; pairs split left/right.
  const footerJustify = ctas.length <= 1 ? 'dome-justify-end' : 'dome-justify-between'

  const footerContent = (
    <div className={`package-entry-modal__footer dome-d-flex ${footerJustify} dome-align-center dome-gap12`} style={{marginTop: 18}}>
      {ctas.map((cta, i) => (
        <button
          key={i}
          type="button"
          className={cta.className || 'dome-btn dome-btn-base'}
          onClick={() => handleCta(cta)}
        >
          {cta.label}
        </button>
      ))}
    </div>
  )

  return (
    <EventModal
      modalStyles={modalStyles || { width: 500, padding: '36px 60px 24px' }}
      modalId={modalId}
      headerContent={headerContent}
      bodyContent={bodyContent}
      footerContent={footerContent}
    />
  )
}
